/**
 * Fetch wrapper.
 * - VITE_API_BASE_URL prefixes every request. In dev it's blank (vite
 *   proxies /api to :8787); in prod it's the absolute Worker URL.
 * - Production: sends the Microsoft sign-in token (lib/auth.ts). A 401 gets
 *   one forced refresh, then a fresh sign-in.
 * - Local dev: sends X-Dev-Email, which the API honours only on localhost.
 */
import { DEV_AUTH, accessToken, signIn } from "./auth.ts";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const DEV_EMAIL = import.meta.env.VITE_DEV_EMAIL ?? "johnnyng@marchproducts.com";

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PUT", path, body);
}

export async function apiDelete<T>(path: string): Promise<T> {
  return request<T>("DELETE", path);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const send = async (force: boolean) => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (DEV_AUTH) {
      headers["X-Dev-Email"] = DEV_EMAIL;
    } else {
      const token = await accessToken(force);
      if (!token) return signIn();
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(API_BASE + path, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  };
  let res = await send(false);
  if (res.status === 401 && !DEV_AUTH) {
    res = await send(true);
    if (res.status === 401) return signIn();
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${method} ${path} — ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** The readable part of an API error: the JSON body's `detail` (or `error`), without method, path and status. */
export function errorText(e: unknown): string {
  const m = String((e as Error)?.message ?? e).match(/^\w+ \S+ — \d+ (.*)$/s);
  if (!m) return String((e as Error)?.message ?? e);
  try {
    const body = JSON.parse(m[1]) as { detail?: string; error?: string };
    return body.detail ?? body.error ?? m[1];
  } catch {
    return m[1];
  }
}
